import { Event } from '@geovelo-frontends/commons';
import { Box, DialogProps, Typography } from '@mui/material';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppContext } from '../../app/context';
import leaderboardIllus from '../../assets/img/bttf/leaderboard-illus.png';
import levelsIllus from '../../assets/img/bttf/levels-illus.png';
import logoBTTF from '../../assets/img/bttf/logo-bttf.svg';
import { Button, ChevronLeftIcon, Dialog } from '../../components';
import { environment } from '../../environment';
import useOfferDialog from '../../hooks/offer-dialog';

function EventDialog({
  event,
  surveyAnswerKey,
  ...props
}: Omit<DialogProps, 'onClose'> & {
  event: Event | null;
  onClose: () => void;
  surveyAnswerKey: string | null;
}): React.JSX.Element {
  const [step, setStep] = useState<'registered' | 'impact' | 'leaderboard'>('registered');
  const {
    user: { employee },
  } = useContext(AppContext);
  const { t } = useTranslation();
  useOfferDialog(props.open, surveyAnswerKey);

  if (event?.code?.toLowerCase() === 'bttf') {
    return (
      <Dialog disableBackgroundClick fullWidth maxWidth="sm" {...props}>
        {step === 'registered' ? (
          <Box display="flex" flexDirection="column" gap={3}>
            <Box flexShrink={0}>
              <img height={70} src={logoBTTF} />
            </Box>
            <Box display="flex" flexDirection="column" gap={1}>
              {employee?.isCollaborator ? (
                <Typography fontSize="1.25rem" fontWeight={600}>
                  Bienvenue sur le challenge Bike to the Future&nbsp;!
                </Typography>
              ) : (
                <Typography fontSize="1.25rem" fontWeight={600}>
                  Vous êtes inscrit au challenge Bike to the Future&nbsp;!
                </Typography>
              )}
              <Typography>
                Du 15 au 28 septembre, préparez-vous à pédaler tous ensemble&nbsp;!
              </Typography>
            </Box>
            <Box
              alignItems="center"
              columnGap={2}
              display="flex"
              flexWrap="wrap"
              justifyContent="flex-end"
              rowGap={1}
            >
              {employee?.isAdminOrAnimator && (
                <Button
                  color="primary"
                  href={environment.bttf.communicationKitUrl}
                  rel="noreferrer"
                  target="_blank"
                  variant="text"
                >
                  {t(
                    'companies.pages.bicycle_compensation.congratulations_dialog.actions.see_communication_kit',
                  )}
                </Button>
              )}
              <Button color="primary" onClick={() => setStep('impact')} variant="contained">
                {t('commons.actions.next')}
              </Button>
            </Box>
          </Box>
        ) : step === 'impact' ? (
          <Box display="flex" flexDirection="column" gap={3}>
            <Box flexShrink={0}>
              <img src={levelsIllus} width="100%" />
            </Box>
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography fontSize="1.25rem" fontWeight={600}>
                Bâtissez ensemble un futur plus désirable
              </Typography>
              <Typography>
                Roulez et cumulez des points d'impact tous ensemble pour passer des niveaux et
                atteindre la cité idéale
              </Typography>
            </Box>
            <Box alignItems="center" display="flex" gap={2} justifyContent="flex-end">
              <Button
                color="primary"
                onClick={() => setStep('registered')}
                startIcon={<ChevronLeftIcon />}
                variant="text"
              >
                {t('commons.actions.prev')}
              </Button>
              <Button color="primary" onClick={() => setStep('leaderboard')} variant="contained">
                {t('commons.actions.next')}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={3}>
            <Box flexShrink={0}>
              <img src={leaderboardIllus} width="100%" />
            </Box>
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography fontSize="1.25rem" fontWeight={600}>
                Montez dans les classements
              </Typography>
              <Typography>
                Gagnez des points en roulant avec l'application Geovelo. Bonus pour votre 1er trajet
                vélo, vos trajets domicile-travail, et si vous invitez des collègues
              </Typography>
            </Box>
            <Box alignItems="center" display="flex" gap={2} justifyContent="flex-end">
              <Button
                color="primary"
                onClick={() => setStep('impact')}
                startIcon={<ChevronLeftIcon />}
                variant="text"
              >
                {t('commons.actions.prev')}
              </Button>
              <Button color="primary" onClick={() => props.onClose()} variant="contained">
                {t('commons.highlighted_event_card.actions.display')}
              </Button>
            </Box>
          </Box>
        )}
      </Dialog>
    );
  }

  return <></>;
}

export default EventDialog;
